import { useEffect, useRef } from "react";

import type { TRealtimeTicket } from "../services/realtime.service";
import type { TMessage, TTurnInfo } from "../types/agent";

const REALTIME_EVENT = {
	CONNECTED: "control.connected",
	HEARTBEAT: "control.heartbeat",
	SUBSCRIBED: "control.subscribed",
	TURN_APPENDED: "paul.turn.appended",
	TURN_STATUS: "paul.turn.status",
	TURN_DONE: "paul.turn.done",
	TURN_ERROR: "paul.turn.error",
} as const;

const TERMINAL_TURN_STATUSES = new Set([
	"ready",
	"done",
	"completed",
	"error",
	"failed",
]);
const SUCCESSFUL_TURN_STATUSES = new Set([
	"ready",
	"done",
	"completed",
]);
const FAILED_TURN_STATUSES = new Set(["error", "failed"]);

const HEARTBEAT_WATCHDOG_MS = 45_000;
const MAX_RECONNECT_DELAY_MS = 10_000;
const FALLBACK_POLL_INTERVAL_MS = 60_000;

type TRealtimeEnvelope<TData = Record<string, unknown>> = {
	type: string;
	channel: string | null;
	subject?: string | null;
	data: TData;
};

type TPaulTurnStatusData = {
	phase: "thinking" | "running";
	attempt?: number;
};

type TUsePaulTurnRealtimeOptions = {
	conversationId: string;
	turnIdx: number | null;
	onSuccess: (
		data: TMessage,
		convStatus: TTurnInfo["conv_status"],
		turnMessages: Array<TMessage>
	) => void;
	onStatus?: (status: TTurnInfo["conv_status"]) => void;
	onError: (error: unknown) => void;
	onAppended?: (numTurns?: number) => void;
	/** Reconcile conversation updates on connect and during idle fallback polls. */
	onSync?: () => void;
	agentService: {
		getTurnInfo: (
			convId: string,
			turnIdx: number
		) => Promise<TTurnInfo>;
	};
	realtimeService: {
		createTicket: (
			channels: Array<string>
		) => Promise<TRealtimeTicket>;
		getStreamUrl: (token: string) => string;
	};
	enabled: boolean;
	maxWaitTime?: number;
};

const parseEnvelope = <TData>(
	event: Event
): TRealtimeEnvelope<TData> | null => {
	try {
		return JSON.parse(
			(event as MessageEvent<string>).data
		) as TRealtimeEnvelope<TData>;
	} catch {
		return null;
	}
};

const isTerminalTurn = (turnInfo: TTurnInfo): boolean => {
	return TERMINAL_TURN_STATUSES.has(
		turnInfo.conv_status.status.toLowerCase()
	);
};

const isFailedTurn = (turnInfo: TTurnInfo): boolean => {
	return FAILED_TURN_STATUSES.has(
		turnInfo.conv_status.status.toLowerCase()
	);
};

export const isPaulTurnSuccessStatus = (
	status?: string
): boolean => {
	return SUCCESSFUL_TURN_STATUSES.has(
		status?.toLowerCase() ?? ""
	);
};

const getRealtimeStatus = (
	data: TPaulTurnStatusData,
	turnIdx: number
): TTurnInfo["conv_status"] => {
	const isThinking = data.phase === "thinking";
	return {
		status: isThinking ? "Thinking" : "Running",
		status_message: isThinking
			? "Paul is thinking through your request."
			: "Paul is running its analysis.",
		turn_idx: turnIdx,
		...(typeof data.attempt === "number" && {
			attempt_idx: Math.max(0, data.attempt - 1),
		}),
	};
};

/** Receives Paul updates over SSE, with periodic HTTP reconciliation. */
export const usePaulTurnRealtime = ({
	conversationId,
	turnIdx,
	onSuccess,
	onStatus,
	onError,
	onAppended,
	onSync,
	agentService,
	realtimeService,
	enabled,
	maxWaitTime = 300_000,
}: TUsePaulTurnRealtimeOptions): void => {
	const onSuccessRef = useRef(onSuccess);
	const onStatusRef = useRef(onStatus);
	const onErrorRef = useRef(onError);
	const onAppendedRef = useRef(onAppended);
	const onSyncRef = useRef(onSync);
	const turnIdxRef = useRef(turnIdx);
	const fetchTurnInfoRef = useRef<
		| ((options?: {
				fromCatchUp?: boolean;
				ignoreFetchError?: boolean;
		  }) => Promise<void>)
		| null
	>(null);
	const failRef = useRef<((error: unknown) => void) | null>(
		null
	);
	const turnTimeoutRef = useRef<ReturnType<
		typeof setTimeout
	> | null>(null);

	useEffect(() => {
		onSuccessRef.current = onSuccess;
		onStatusRef.current = onStatus;
		onErrorRef.current = onError;
		onAppendedRef.current = onAppended;
		onSyncRef.current = onSync;
		turnIdxRef.current = turnIdx;
	});

	useEffect(() => {
		if (!enabled || !conversationId) {
			return;
		}

		const channel = `conv:${conversationId}`;
		const lifecycleController = new AbortController();
		let isConnecting = false;
		let requestInFlight = false;
		let reconnectAttempt = 0;
		let eventSource: EventSource | null = null;
		let reconnectTimeout: ReturnType<
			typeof setTimeout
		> | null = null;
		let watchdogTimeout: ReturnType<
			typeof setTimeout
		> | null = null;
		let fallbackPollInterval: ReturnType<
			typeof setInterval
		> | null = null;

		const clearTimer = (
			timer: ReturnType<typeof setTimeout> | null
		) => {
			if (timer) {
				clearTimeout(timer);
			}
		};

		const closeEventSource = () => {
			eventSource?.close();
			eventSource = null;
			clearTimer(watchdogTimeout);
			watchdogTimeout = null;
		};

		const stop = () => {
			lifecycleController.abort();
			closeEventSource();
			clearTimer(reconnectTimeout);
			reconnectTimeout = null;
			if (fallbackPollInterval !== null) {
				clearInterval(fallbackPollInterval);
				fallbackPollInterval = null;
			}
		};

		const fail = (
			error: unknown,
			keepConnection = Boolean(onAppendedRef.current)
		) => {
			if (lifecycleController.signal.aborted) {
				return;
			}
			if (!keepConnection) {
				stop();
			}
			onErrorRef.current(error);
		};
		failRef.current = fail;

		const matchesTurn = (
			envelope: TRealtimeEnvelope<unknown> | null
		): boolean => {
			const currentTurnIdx = turnIdxRef.current;
			return (
				currentTurnIdx !== null &&
				envelope?.channel === channel &&
				envelope.subject === `turn:${currentTurnIdx}`
			);
		};

		const fetchTurnInfo = async ({
			fromCatchUp = false,
			ignoreFetchError = false,
		} = {}) => {
			if (lifecycleController.signal.aborted) {
				return;
			}
			const requestedTurnIdx = turnIdxRef.current;
			if (requestedTurnIdx === null || requestInFlight) {
				return;
			}

			requestInFlight = true;
			try {
				const turnInfo = await agentService.getTurnInfo(
					conversationId,
					requestedTurnIdx
				);
				// The effect can be cleaned up while this request is pending.
				if (
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
					lifecycleController.signal.aborted ||
					turnIdxRef.current !== requestedTurnIdx
				) {
					return;
				}
				const modelMessage = turnInfo.messages.find(
					(message) => message.role === "model"
				);
				if (isFailedTurn(turnInfo)) {
					fail(
						new Error(
							modelMessage?.error_msg ??
								turnInfo.conv_status.status_message ??
								"Paul could not complete this request."
						)
					);
					return;
				}

				if (modelMessage) {
					onSuccessRef.current(
						modelMessage,
						turnInfo.conv_status,
						turnInfo.messages
					);
				}

				if (isTerminalTurn(turnInfo)) {
					if (!modelMessage) {
						fail(
							new Error(
								"Paul finished without returning a response."
							)
						);
						return;
					}
					if (!onAppendedRef.current) {
						stop();
					}
					return;
				}

				// On connect the turn is normally still in flight; a non-terminal catch-up is expected -
				// exit quietly and let the live TURN_DONE resolve it. The live callers still throw.
				if (fromCatchUp) {
					onStatusRef.current?.(turnInfo.conv_status);
					return;
				}

				throw new Error(
					"Paul's terminal event arrived before its final response was available."
				);
			} catch (error) {
				// A failed backup request must not mark a running turn as failed.
				if (
					!ignoreFetchError &&
					turnIdxRef.current === requestedTurnIdx
				) {
					fail(error);
				}
			} finally {
				requestInFlight = false;
			}
		};
		fetchTurnInfoRef.current = fetchTurnInfo;

		const resetWatchdog = () => {
			clearTimer(watchdogTimeout);
			watchdogTimeout = setTimeout(() => {
				scheduleReconnect();
			}, HEARTBEAT_WATCHDOG_MS);
		};

		const scheduleReconnect = () => {
			if (
				lifecycleController.signal.aborted ||
				reconnectTimeout
			) {
				return;
			}

			closeEventSource();
			reconnectAttempt += 1;
			const reconnectDelay = Math.min(
				1_000 * 2 ** (reconnectAttempt - 1),
				MAX_RECONNECT_DELAY_MS
			);
			reconnectTimeout = setTimeout(() => {
				reconnectTimeout = null;
				void connect();
			}, reconnectDelay);
		};

		const connect = async () => {
			if (
				lifecycleController.signal.aborted ||
				isConnecting ||
				eventSource
			) {
				return;
			}

			isConnecting = true;
			try {
				const ticket = await realtimeService.createTicket([
					channel,
				]);
				// The effect can be cleaned up while this request is pending.
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				if (lifecycleController.signal.aborted) {
					return;
				}
				if (
					!ticket.token ||
					!ticket.channels.includes(channel)
				) {
					fail(
						new Error(
							"Realtime access was not granted for this conversation."
						),
						false
					);
					return;
				}

				const source = new EventSource(
					realtimeService.getStreamUrl(ticket.token)
				);
				eventSource = source;

				source.addEventListener(
					REALTIME_EVENT.CONNECTED,
					(event) => {
						const envelope = parseEnvelope<{
							connectionId: string;
						}>(event);
						if (!envelope?.data.connectionId) {
							return;
						}
						reconnectAttempt = 0;
						resetWatchdog();
						onSyncRef.current?.();
						// Catch-up fetch: reconcile the turn on connect so a paul.turn.done that
						// fired during the subscribe gap isn't missed (else we'd spin to maxWaitTime).
						// requestInFlight-guarded; terminal turns resolve immediately, non-terminal exits quietly.
						void fetchTurnInfo({ fromCatchUp: true });
					}
				);
				source.addEventListener(
					REALTIME_EVENT.TURN_APPENDED,
					(event) => {
						const envelope = parseEnvelope<{
							num_turns?: number;
						}>(event);
						if (!envelope || envelope.channel !== channel) {
							return;
						}
						resetWatchdog();
						onAppendedRef.current?.(
							envelope.data.num_turns
						);
					}
				);
				source.addEventListener(
					REALTIME_EVENT.TURN_STATUS,
					(event) => {
						const envelope =
							parseEnvelope<TPaulTurnStatusData>(event);
						if (!envelope || !matchesTurn(envelope)) {
							return;
						}
						resetWatchdog();
						onStatusRef.current?.(
							getRealtimeStatus(
								envelope.data,
								turnIdxRef.current as number
							)
						);
					}
				);
				source.addEventListener(
					REALTIME_EVENT.TURN_DONE,
					(event) => {
						const envelope = parseEnvelope(event);
						if (!matchesTurn(envelope)) {
							return;
						}
						resetWatchdog();
						void fetchTurnInfo();
					}
				);
				source.addEventListener(
					REALTIME_EVENT.TURN_ERROR,
					(event) => {
						const envelope = parseEnvelope(event);
						if (!matchesTurn(envelope)) {
							return;
						}
						resetWatchdog();
						void fetchTurnInfo();
					}
				);
				source.addEventListener(
					REALTIME_EVENT.HEARTBEAT,
					resetWatchdog
				);
				source.addEventListener(
					REALTIME_EVENT.SUBSCRIBED,
					resetWatchdog
				);
				source.onerror = scheduleReconnect;
				resetWatchdog();
			} catch {
				scheduleReconnect();
			} finally {
				isConnecting = false;
			}
		};

		// Keep this independent of SSE activity: heartbeats can continue even when
		// a terminal event or a backend-authored turn was missed.
		fallbackPollInterval = setInterval(() => {
			if (turnIdxRef.current === null) {
				onSyncRef.current?.();
				return;
			}
			void fetchTurnInfo({
				fromCatchUp: true,
				ignoreFetchError: true,
			});
		}, FALLBACK_POLL_INTERVAL_MS);

		void connect();

		return () => {
			stop();
			if (fetchTurnInfoRef.current === fetchTurnInfo) {
				fetchTurnInfoRef.current = null;
			}
			if (failRef.current === fail) {
				failRef.current = null;
			}
		};
	}, [
		agentService,
		conversationId,
		enabled,
		realtimeService,
	]);

	useEffect(() => {
		clearTimeout(turnTimeoutRef.current ?? undefined);
		turnTimeoutRef.current = null;

		if (!enabled || !conversationId || turnIdx === null) {
			return;
		}

		turnTimeoutRef.current = setTimeout(() => {
			failRef.current?.(
				new Error("Waiting for Paul's response timed out.")
			);
		}, maxWaitTime);

		return () => {
			clearTimeout(turnTimeoutRef.current ?? undefined);
			turnTimeoutRef.current = null;
		};
	}, [conversationId, enabled, maxWaitTime, turnIdx]);

	useEffect(() => {
		if (!enabled || !conversationId || turnIdx === null) {
			return;
		}

		void fetchTurnInfoRef.current?.({ fromCatchUp: true });
	}, [conversationId, enabled, turnIdx]);
};
